import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/stores/redux/store';

export interface Artifact {
    id: string;
    created_at: string;
    updated_at?: string;
    name: string;
    description?: string;
    type: 'SLIDES' | 'NOTES' | 'AUDIO' | 'VIDEO' | 'DATASHEET' | 'IMAGES' | 'DOCX' | 'HTML';
    fk_user_id: string;
    fk_workspace_id: string;
    fk_topic_id: string;
    fk_folder_id?: string | null;
    folder?: { id: string; name: string } | null;
    visibility: 'private' | 'workspace' | 'public';
    content?: any;
    status?: string;
    embedding_model?: string;
    file_size?: number;
    thumbnail_url?: string;
    slide_count?: number;
    image_count?: number;
    editor_mode?: 'visual' | 'code' | 'split' | 'preview';
}

interface ArtifactsState {
    artifacts: Artifact[];
    activeArtifact: string | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

// Track in-flight requests to prevent duplicate calls
const inFlightRequests: { [key: string]: Promise<any> } = {};

export const fetchArtifacts = createAsyncThunk(
    'artifacts/fetchArtifacts',
    async (payload: { topicId?: string; workspaceOnly?: boolean } | undefined, { getState }) => {
        const state = getState() as RootState;
        const workspaceId = state.activeWorkspace?.id;
        // If workspaceOnly is true, don't filter by topic (fetch all workspace artifacts)
        // Otherwise, use provided topicId or fall back to active topic
        const topicId = payload?.workspaceOnly ? undefined : (payload?.topicId || state.topics?.activeTopic);

        if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null') {
            console.warn('fetchArtifacts called without valid active workspace:', workspaceId);
            return [];
        }

        // Check if there's already an in-flight request
        const requestKey = `artifacts_${workspaceId}_${topicId || 'all'}`;
        if (inFlightRequests[requestKey]) {
            return inFlightRequests[requestKey];
        }

        // Build query params
        const params = new URLSearchParams();
        params.append('workspaceId', workspaceId);
        if (topicId) {
            params.append('topicId', topicId);
        }

        // Create and store the promise
        const requestPromise = fetch(`/api/artifacts?${params.toString()}`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch artifacts');
                return res.json();
            })
            .finally(() => {
                delete inFlightRequests[requestKey];
            });

        inFlightRequests[requestKey] = requestPromise;
        return requestPromise;
    }
);

export const createArtifact = createAsyncThunk(
    'artifacts/createArtifact',
    async (payload: Partial<Artifact>) => {
        const response = await fetch('/api/artifacts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Failed to create artifact');
        return response.json();
    }
);

export const updateArtifact = createAsyncThunk(
    'artifacts/updateArtifact',
    async (payload: { id: string } & Partial<Artifact>) => {
        const { id, ...data } = payload;
        const response = await fetch(`/api/artifacts/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update artifact');
        return response.json();
    }
);

export const deleteArtifact = createAsyncThunk(
    'artifacts/deleteArtifact',
    async (id: string) => {
        const response = await fetch(`/api/artifacts/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete artifact');
        return id;
    }
);

const artifactsSlice = createSlice({
    name: 'artifacts',
    initialState: {
        artifacts: [],
        activeArtifact: null,
        status: 'idle',
        error: null
    } as ArtifactsState,
    reducers: {
        setArtifacts: (state, action: PayloadAction<Artifact[]>) => {
            state.artifacts = action.payload;
        },
        setActiveArtifact: (state, action: PayloadAction<string | null>) => {
            state.activeArtifact = action.payload;
        },
        addArtifact: (state, action: PayloadAction<Artifact>) => {
            state.artifacts.push(action.payload);
        },
        updateArtifactLocal: (state, action: PayloadAction<Partial<Artifact> & { id: string }>) => {
            const index = state.artifacts.findIndex(a => a.id === action.payload.id);
            if (index !== -1) {
                // Update existing artifact
                state.artifacts[index] = { ...state.artifacts[index], ...action.payload };
            } else {
                // Artifact not in store (e.g., opened directly via URL) - add it
                state.artifacts.push(action.payload as Artifact);
            }
        },
        removeArtifact: (state, action: PayloadAction<string>) => {
            state.artifacts = state.artifacts.filter(a => a.id !== action.payload);
        }
    },
    extraReducers: (builder) => {
        // Fetch artifacts
        builder.addCase(fetchArtifacts.pending, (state) => {
            state.status = 'loading';
        });
        builder.addCase(fetchArtifacts.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.artifacts = action.payload;
        });
        builder.addCase(fetchArtifacts.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.error.message || null;
        });

        // Create artifact
        builder.addCase(createArtifact.fulfilled, (state, action) => {
            state.artifacts.push(action.payload);
        });

        // Update artifact
        builder.addCase(updateArtifact.fulfilled, (state, action) => {
            const index = state.artifacts.findIndex(a => a.id === action.payload.id);
            if (index !== -1) {
                state.artifacts[index] = action.payload;
            }
        });

        // Delete artifact
        builder.addCase(deleteArtifact.fulfilled, (state, action) => {
            state.artifacts = state.artifacts.filter(a => a.id !== action.payload);
        });
    }
});

export const {
    setArtifacts,
    setActiveArtifact,
    addArtifact,
    updateArtifactLocal,
    removeArtifact
} = artifactsSlice.actions;

export default artifactsSlice.reducer;
